import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axiosInstance from '../../../constants/axiosInstance';
import { toast } from 'react-toastify';

export interface NotificationSettings {
  [moduleId: string]: boolean;
}

interface ApiResponse<T> {
  data: T;
  success: boolean;
  message?: string;
}

const NOTIFICATION_SETTINGS_URL = '/notification/getUserNotificationsList';
const NOTIFICATION_MODIFY_URL = '/notification/setUserNotifications';
const NOTIFICATION_SETTINGS_QUERY_KEY = 'notificationSettings';

const fetchNotificationSettings = async (): Promise<NotificationSettings> => {
  const { data } = await axiosInstance().post<ApiResponse<NotificationSettings>>(NOTIFICATION_SETTINGS_URL);
  return data?.userNotificationsModuleList;
};

const updateNotificationSettings = async (
  settings: NotificationSettings
): Promise<NotificationSettings> => {
  const { data } = await axiosInstance().post<ApiResponse<NotificationSettings>>(
    NOTIFICATION_MODIFY_URL,
    settings
  );
  return data;
};

export const useNotificationSettings = () => {
  const queryClient = useQueryClient();
  const queryKey = [NOTIFICATION_SETTINGS_QUERY_KEY];
  const {
    data: settings,
    error,
    isLoading,
  } = useQuery<NotificationSettings, Error>({
    queryKey: queryKey,
    queryFn: fetchNotificationSettings,
    retry: 2,
    refetchOnWindowFocus: false,
  });

  const mutation = useMutation({
    mutationFn: updateNotificationSettings,
    onSuccess: data => {
      // queryClient.invalidateQueries({queryKey: [NOTIFICATION_SETTINGS_QUERY_KEY]})
    },
    onMutate: async (newSettings: NotificationSettings) => {
      await queryClient.cancelQueries({ queryKey: queryKey });
      const previousSettings = queryClient.getQueryData<NotificationSettings>(queryKey);
      queryClient.setQueryData(queryKey, { ...previousSettings, selectedModules: newSettings?.moduleList });
      return { previousSettings };
    },
    onError: (err, newSettings, context) => {
      if (context?.previousSettings) {
        queryClient.setQueryData(queryKey, context.previousSettings);
        toast.error("Failed to update settings. Reverting changes.");
      } else {
        toast.error("Failed to update settings.");
      }
    },
  });

  const updateSettings = (newSettings: NotificationSettings) => {
    mutation.mutate(newSettings);
  };

  return {
    settings: settings ?? {},
    isLoading,
    error,
    isUpdating: mutation.isPending,
    updateError: mutation.error,
    updateSettings,
  };
};
